import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MailPlus } from 'lucide-react';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/toast';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import { useInvitationStatus, useResendInvitation } from 'hooks/useInvitation';
import { getErrorMessage } from 'constants/errorCodes';
import { formatDateTime } from 'utils/formatDate';
import { ApiError } from 'utils/unwrapResponse';
import type { InvitationTarget } from 'api/invitationApi';

const HINT_DATE_FORMAT = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' } as const;

interface ResendInvitationButtonProps {
  target: InvitationTarget;
  email: string;
}

export const ResendInvitationButton = ({ target, email }: ResendInvitationButtonProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const hintId = useId();
  const { data: status, isLoading, refetch } = useInvitationStatus(target);
  const resend = useResendInvitation(target);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  // `isPending` lags a render behind the click; the ref is what makes a double click send once.
  const inFlight = useRef(false);

  const { state, expiresAt } = status ?? { state: undefined, expiresAt: null };

  if (state === 'inactive') return null;

  const linkValid = state === 'link_valid';
  const closeModal = () => {
    setOpen(false);
    setError(null);
  };

  const confirm = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setError(null);
    resend.mutate(undefined, {
      onSuccess: ({ queued }) => {
        closeModal();
        if (queued) {
          setSent(true);
          toast.success(t('Invitation resent to {{email}}.', { email }));
        } else {
          toast.error(t('Request recorded, but the email could not be sent. Try again in a few minutes.'));
        }
      },
      onError: (err) => {
        const code = err instanceof ApiError ? err.errorCode : null;
        setError(code ? getErrorMessage(t, code) : t('An error occurred. Please try again later.'));
        if (err instanceof ApiError && err.status === 409) refetch();
      },
      onSettled: () => {
        inFlight.current = false;
      },
    });
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={isLoading || linkValid || sent}
        aria-describedby={linkValid ? hintId : undefined}
        onClick={() => setOpen(true)}
      >
        <MailPlus className="mr-2 h-4 w-4" aria-hidden="true" />
        {sent && !linkValid ? t('Invitation sent') : t('Resend the invitation')}
      </Button>
      {linkValid && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {t('Link valid until {{date}}', { date: formatDateTime(expiresAt, HINT_DATE_FORMAT) })}
        </p>
      )}
      <ConfirmDeleteDialog
        open={open}
        tone="warning"
        title={t('Resend the invitation?')}
        description={t(
          'Are you sure you want to resend an invitation link to {{email}}? The previous link will no longer work.',
          { email }
        )}
        confirmLabel={t('Resend')}
        loading={resend.isPending}
        error={error}
        onConfirm={confirm}
        onClose={closeModal}
      />
    </>
  );
};
